import type { TOOL_NAMES } from 'js/src/agent/toolNames';
import {
    estimateTextTokens,
    isContextMessageOversized,
    OVERSIZED_CONTEXT_MESSAGE_FRACTION,
} from './tokenEstimation';
import { getContextLimit } from './types';

import { ErrorReporter } from 'js/src/libs/error-reporter';

/** The injection path that produced an oversized render. Part of the Sentry fingerprint. */
export type OversizedRenderSource =
    | 'context_message'
    | 'video_state_update'
    | typeof TOOL_NAMES.getVideoState;

/**
 * Per-agent overrides for the oversized fraction, keyed by task name (see each RootAgent's
 * getTaskName). Agents absent here use OVERSIZED_CONTEXT_MESSAGE_FRACTION. This is the single place
 * to hold a specific surface to a different bar — e.g. raising the threshold for an agent that's
 * being sunset so its noise doesn't bury the signals we still act on.
 */
const OVERSIZED_FRACTION_BY_AGENT: Record<string, number> = {
    // improve_media_agent runs on gpt-4.1-mini (100k) and injects the full media-candidate catalog,
    // so its render routinely lands at 0.51–0.66 of the window without anything being wrong. It's
    // slated for sunset, so we hold it to a higher bar than the shared 0.5 — only surface it if it's
    // genuinely close to wedging — to keep its noise from burying the toolbox signal we act on.
    improve_media_agent: 0.75,
};

export function oversizedFractionForAgent(agentName: string): number {
    return OVERSIZED_FRACTION_BY_AGENT[agentName] ?? OVERSIZED_CONTEXT_MESSAGE_FRACTION;
}

/**
 * Flag a serialized render that, on its own, takes a dangerous share of the model's context window.
 * We no longer truncate a render's fields (the agent needs the full content to read and restate it),
 * so an unusually large one can crowd out the conversation — or, past the window, wedge it. Rather
 * than silently clip fields, surface these to Sentry so we can see when a real mitigation is needed.
 *
 * `source` names the injection path and `agentName` the root agent (task) that produced it; together
 * they fingerprint the report into a distinct Sentry issue per surface, so one noisy agent can't
 * bury another's signal. Each agent's threshold is resolved via oversizedFractionForAgent. No-op when
 * the render is a normal size.
 */
export function reportIfVideoStateOversized(
    description: string,
    model: string | undefined,
    source: OversizedRenderSource,
    agentName: string,
): void {
    const contextLimit = getContextLimit(model ?? '');
    const fraction = oversizedFractionForAgent(agentName);
    if (!isContextMessageOversized(description, contextLimit, fraction)) {
        return;
    }
    const estimatedTokens = estimateTextTokens(description);
    ErrorReporter.capture(new Error(`Agent render is oversized: ${agentName} / ${source}`), {
        // One Sentry issue per (agent, source) so each surface is triageable at a glance and can't
        // drown out the others under a single shared grouping.
        fingerprint: ['agent-oversized-render', agentName, source],
        tags: { agent: agentName, oversized_source: source },
        extra: {
            source,
            agent: agentName,
            model,
            context_limit: contextLimit,
            fraction_threshold: fraction,
            // Report raw chars (never scrubbed) alongside the ratio; the token estimate is chars/4.
            // A "*_tokens" key gets redacted to null by Sentry's default data scrubbing.
            estimated_render_chars: description.length,
            fraction_of_limit: estimatedTokens / contextLimit,
        },
    });
}
